import React from 'react';
import { Modal } from '@/components/ui';

// ResponsiveModal — thin wrapper kept for its existing call sites. It now delegates to the
// shared `ui/Modal` primitive instead of hand-rolling an overlay, so these modals get the
// focus trap, body-scroll lock, ESC handling, mobile full-screen sheet, pinned header/footer
// and transitions for free. Prefer using `Modal` directly in new code.
interface ResponsiveModalProps {
  /**
   * V2-074 / EDIT-01: show the form read-only – values as label / value text, with no input
   * boxes, borders, drop-down arrows or required asterisks (`.wrap-readonly-form` in
   * tailwind.css). For a locked task or a View-only record.
   */
  readOnly?: boolean;
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  footer?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  /** When false, the page behind stays scrollable. */
  lockDocument?: boolean;
}

/** Legacy size names → shared primitive sizes (~400px / ~700px / ~1200px as before). */
const SIZE_MAP: Record<'small' | 'medium' | 'large', 'sm' | 'lg' | '2xl'> = {
  small: 'sm',
  medium: 'lg',
  large: '2xl',
};

const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  children,
  isOpen,
  onClose,
  readOnly = false,
  title,
  footer,
  size = 'large',
  lockDocument = true,
}) => (
  // No `scrollBody` escape hatch: Modal.Body is deliberately the only scrolling region, and the
  // prop that used to disable it never worked — `tw-overflow-visible` lost to Body's own
  // `tw-overflow-y-auto` on stylesheet order, so every caller scrolled regardless. Removed
  // rather than repaired: a modal whose body cannot scroll is how content gets clipped out of
  // reach in the first place.
  <Modal show={isOpen} onHide={onClose} size={SIZE_MAP[size]} lockDocument={lockDocument}>
    <Modal.Header closeButton>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body className={readOnly ? 'wrap-readonly-form' : ''}>{children}</Modal.Body>
    {footer && <Modal.Footer>{footer}</Modal.Footer>}
  </Modal>
);

export default ResponsiveModal;
